/**
 * Grammar lesson component.
 *
 * Displays grammar lesson content with markdown rendering. Tracks scroll
 * position to detect when user has viewed the entire lesson and triggers
 * completion callback.
 *
 * @module GrammarLesson
 */

import { useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { markdownComponents } from './MarkdownComponents';

interface GrammarLessonProps {
  readonly content: string | null;
  readonly activeTab: string;
  readonly onLessonCompleted: () => void;
}

/**
 * Component for displaying grammar lesson content with markdown rendering
 */
export function GrammarLesson({ content, activeTab, onLessonCompleted }: GrammarLessonProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  // Auto-mark lesson as completed when scrolled 80%
  useEffect(() => {
    if (!contentRef.current || activeTab !== 'lesson') return;

    let lessonCompleted = false;
    const handleScroll = () => {
      const element = contentRef.current;
      if (!element || lessonCompleted) return;

      const scrollPercentage =
        (element.scrollTop / (element.scrollHeight - element.clientHeight)) * 100;
      if (scrollPercentage >= 80 && !lessonCompleted) {
        lessonCompleted = true;
        onLessonCompleted();
      }
    };

    const element = contentRef.current;
    element.addEventListener('scroll', handleScroll);
    return () => element.removeEventListener('scroll', handleScroll);
  }, [activeTab, onLessonCompleted]);

  return (
    <div
      ref={contentRef}
      className="w-full prose prose-slate dark:prose-invert prose-headings:text-primary prose-strong:text-primary/90 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline"
    >
      {content ? (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {content}
        </ReactMarkdown>
      ) : (
        <p className="text-muted-foreground">No content available</p>
      )}
    </div>
  );
}

